// src/context/QueryContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../services/api';
import { useAuth } from './AuthContext';

const QueryContext = createContext(null);

export const useQueryContext = () => {
    const context = useContext(QueryContext);
    if (!context) {
        throw new Error('useQueryContext must be used within QueryProvider');
    }
    return context;
};

export const QueryProvider = ({ children }) => {
    const { user, isAuthenticated, isAdminOrHR, isEmployee } = useAuth();
    const [queries, setQueries] = useState([]);
    const [myQueries, setMyQueries] = useState([]);
    const [selectedQuery, setSelectedQuery] = useState(null);
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [dashboardStats, setDashboardStats] = useState(null);
    const [recentQueries, setRecentQueries] = useState([]);
    const [employeeGroups, setEmployeeGroups] = useState([]);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        pageSize: 10,
        hasNext: false,
        hasPrevious: false
    });
    const [employeePagination, setEmployeePagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        pageSize: 5,
        hasNext: false,
        hasPrevious: false
    });
    const [myQueryStats, setMyQueryStats] = useState(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // Fetch all queries (HR/Admin only)
    const fetchQueries = useCallback(async (params = {}) => {
        if (!isAuthenticated || !isAdminOrHR) return;

        setLoading(true);
        setError(null);
        try {
            const queryParams = new URLSearchParams({
                page: params.page || 1,
                page_size: params.pageSize || 10,
                ...params.filters
            }).toString();

            const response = await api.get(`/api/employee-queries/queries/?${queryParams}`);
            const data = response.results || response.data || [];
            setQueries(data);

            if (response.count) {
                setPagination({
                    currentPage: params.page || 1,
                    totalPages: Math.ceil(response.count / (params.pageSize || 10)),
                    totalItems: response.count,
                    pageSize: params.pageSize || 10,
                    hasNext: !!response.next,
                    hasPrevious: !!response.previous
                });
            }
        } catch (err) {
            setError(err.message || 'Failed to fetch queries');
            console.error('Fetch queries error:', err);
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated, isAdminOrHR]);

    // Fetch my queries (Employee only)
    const fetchMyQueries = useCallback(async () => {
        if (!isAuthenticated || isAdminOrHR) {
            setMyQueries([]);
            return [];
        }

        setLoading(true);
        setError(null);
        try {
            const response = await api.get('/api/employee-queries/my-queries/');
            const data = response.results || response.data || response || [];
            setMyQueries(Array.isArray(data) ? data : [data]);
            return data;
        } catch (err) {
            if (err.message?.includes('Employee profile not found')) {
                setMyQueries([]);
                return [];
            }
            setError(err.message || 'Failed to fetch my queries');
            console.error('Fetch my queries error:', err);
            return [];
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated, isAdminOrHR]);

    // Fetch query details
    const fetchQueryDetails = useCallback(async (queryId) => {
        setLoading(true);
        setError(null);
        try {
            const response = await api.get(`/api/employee-queries/queries/${queryId}/`);
            setSelectedQuery(response);
            return response;
        } catch (err) {
            setError(err.message || 'Failed to fetch query details');
            console.error('Fetch query details error:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch comments for a query
    const fetchComments = useCallback(async (queryId) => {
        setLoading(true);
        try {
            const response = await api.get(`/api/employee-queries/queries/${queryId}/comments/`);
            const data = response.results || response.data || response || [];
            setComments(Array.isArray(data) ? data : [data]);
            return data;
        } catch (err) {
            console.error('Fetch comments error:', err);
            setComments([]);
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch query history
    const fetchQueryHistory = useCallback(async (queryId) => {
        setLoading(true);
        try {
            const response = await api.get(`/api/employee-queries/queries/${queryId}/history/`);
            return response.results || response.data || response || [];
        } catch (err) {
            console.error('Fetch history error:', err);
            return [];
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch dashboard stats (HR/Admin only)
    const fetchDashboardStats = useCallback(async () => {
        if (!isAdminOrHR) return;

        setLoading(true);
        try {
            const response = await api.get('/api/employee-queries/dashboard/stats/');
            setDashboardStats(response);
            return response;
        } catch (err) {
            console.error('Fetch dashboard stats error:', err);
        } finally {
            setLoading(false);
        }
    }, [isAdminOrHR]);

    // Fetch recent queries (HR/Admin only)
    const fetchRecentQueries = useCallback(async () => {
        if (!isAdminOrHR) return;

        try {
            const response = await api.get('/api/employee-queries/recent/');
            const data = response.results || response.data || response || [];
            setRecentQueries(Array.isArray(data) ? data : [data]);
        } catch (err) {
            console.error('Fetch recent queries error:', err);
        }
    }, [isAdminOrHR]);

    // Fetch employee groups (HR/Admin only)
    const fetchEmployeeGroups = useCallback(async (params = {}) => {
        if (!isAdminOrHR) return;

        setLoading(true);
        setError(null);
        try {
            const queryParams = new URLSearchParams({
                page: params.page || 1,
                page_size: params.pageSize || 5,
                ...params.filters
            }).toString();

            const response = await api.get(`/api/employee-queries/employee_groups/?${queryParams}`);
            const data = response.results || response.data || [];
            setEmployeeGroups(data);

            setEmployeePagination({
                currentPage: response.current_page || params.page || 1,
                totalPages: response.total_pages || 1,
                totalItems: response.count || 0,
                pageSize: response.page_size || 5,
                hasNext: response.has_next || false,
                hasPrevious: response.has_previous || false
            });
            return data;
        } catch (err) {
            setError(err.message || 'Failed to fetch employee groups');
            console.error('Fetch employee groups error:', err);
        } finally {
            setLoading(false);
        }
    }, [isAdminOrHR]);

    // Fetch my query stats (Employee only)
    const fetchMyQueryStats = useCallback(async () => {
        if (!isAuthenticated || isAdminOrHR) {
            setMyQueryStats(null);
            return null;
        }

        try {
            const response = await api.get('/api/employee-queries/my-stats/');
            setMyQueryStats(response);
            return response;
        } catch (err) {
            if (err.message?.includes('Employee profile not found')) {
                setMyQueryStats(null);
                return null;
            }
            console.error('Fetch my stats error:', err);
            return null;
        }
    }, [isAuthenticated, isAdminOrHR]);

    // Create a new query
    const createQuery = useCallback(async (data) => {
        setLoading(true);
        setError(null);
        try {
            const formData = new FormData();
            Object.keys(data).forEach(key => {
                if (data[key] !== null && data[key] !== undefined) {
                    formData.append(key, data[key]);
                }
            });

            const response = await api.post('/api/employee-queries/queries/', formData);
            await refreshAllData();
            return response;
        } catch (err) {
            setError(err.message || 'Failed to create query');
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    // Update a query
    const updateQuery = useCallback(async (queryId, data) => {
        setLoading(true);
        setError(null);
        try {
            const response = await api.patch(`/api/employee-queries/queries/${queryId}/`, data);
            await refreshAllData();
            return response;
        } catch (err) {
            setError(err.message || 'Failed to update query');
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    // Delete a query
    const deleteQuery = useCallback(async (queryId) => {
        setLoading(true);
        setError(null);
        try {
            await api.delete(`/api/employee-queries/queries/${queryId}/delete_query/`);
            await refreshAllData();
            return true;
        } catch (err) {
            setError(err.message || 'Failed to delete query');
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    // Add comment to query
    const addComment = useCallback(async (queryId, data) => {
        setLoading(true);
        setError(null);
        try {
            let response;
            if (data instanceof FormData) {
                response = await api.post(
                    `/api/employee-queries/queries/${queryId}/add_comment/`,
                    data
                );
            } else {
                response = await api.post(
                    `/api/employee-queries/queries/${queryId}/add_comment/`,
                    data
                );
            }
            await fetchComments(queryId);
            await refreshAllData();
            return response;
        } catch (err) {
            setError(err.message || 'Failed to add comment');
            throw err;
        } finally {
            setLoading(false);
        }
    }, [fetchComments]);

    // Delete comment (HR/Admin only)
    const deleteComment = useCallback(async (commentId) => {
        if (!isAdminOrHR) return;

        setLoading(true);
        setError(null);
        try {
            await api.delete(`/api/employee-queries/comments/${commentId}/delete_comment/`);
            if (selectedQuery) {
                await fetchComments(selectedQuery.id);
            }
            return true;
        } catch (err) {
            setError(err.message || 'Failed to delete comment');
            throw err;
        } finally {
            setLoading(false);
        }
    }, [isAdminOrHR, selectedQuery, fetchComments]);

    // Assign query to HR (HR/Admin only)
    const assignQuery = useCallback(async (queryId, userId) => {
        if (!isAdminOrHR) return;

        setLoading(true);
        try {
            const response = await api.post(`/api/employee-queries/queries/${queryId}/assign/`, { user_id: userId });
            await refreshAllData();
            return response;
        } catch (err) {
            console.error('Assign query error:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [isAdminOrHR]);

    // Resolve query (HR/Admin only)
    const resolveQuery = useCallback(async (queryId, notes = '') => {
        if (!isAdminOrHR) return;

        setLoading(true);
        try {
            const response = await api.post(`/api/employee-queries/queries/${queryId}/resolve/`, { notes });
            await refreshAllData();
            return response;
        } catch (err) {
            console.error('Resolve query error:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [isAdminOrHR]);

    // Close query (HR/Admin only)
    const closeQuery = useCallback(async (queryId, notes = '') => {
        if (!isAdminOrHR) return;

        setLoading(true);
        try {
            const response = await api.post(`/api/employee-queries/queries/${queryId}/close/`, { notes });
            await refreshAllData();
            return response;
        } catch (err) {
            console.error('Close query error:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [isAdminOrHR]);

    // Reopen query (HR/Admin only)
    const reopenQuery = useCallback(async (queryId) => {
        if (!isAdminOrHR) return;

        setLoading(true);
        try {
            const response = await api.post(`/api/employee-queries/queries/${queryId}/reopen/`);
            await refreshAllData();
            return response;
        } catch (err) {
            console.error('Reopen query error:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [isAdminOrHR]);

    // Refresh all data
    const refreshAllData = useCallback(async () => {
        if (isAdminOrHR) {
            await fetchDashboardStats();
            await fetchRecentQueries();
            await fetchQueries({ page: 1 });
            await fetchEmployeeGroups({ page: 1, pageSize: 5 });
            setMyQueries([]);
            setMyQueryStats(null);
        } else if (isEmployee) {
            await fetchMyQueries();
            await fetchMyQueryStats();
            setQueries([]);
            setEmployeeGroups([]);
        }
        setRefreshTrigger(prev => prev + 1);
    }, [isAdminOrHR, isEmployee, fetchDashboardStats, fetchRecentQueries, fetchQueries, fetchEmployeeGroups, fetchMyQueries, fetchMyQueryStats]);

    // Load initial data
    useEffect(() => {
        if (isAuthenticated) {
            refreshAllData();
        }
    }, [isAuthenticated, refreshAllData]);

    // Get status badge color
    const getStatusBadge = useCallback((status) => {
        const statusMap = {
            'pending': { color: 'red', icon: 'FiClock', label: 'Pending' },
            'in_progress': { color: 'yellow', icon: 'FiRefreshCw', label: 'In Progress' },
            'resolved': { color: 'green', icon: 'FiCheckCircle', label: 'Resolved' },
            'closed': { color: 'blue', icon: 'FiXCircle', label: 'Closed' },
            'rejected': { color: 'gray', icon: 'FiAlertCircle', label: 'Rejected' }
        };
        return statusMap[status] || statusMap['pending'];
    }, []);

    // Get priority badge color
    const getPriorityBadge = useCallback((priority) => {
        const priorityMap = {
            'low': { color: 'blue', label: 'Low' },
            'medium': { color: 'yellow', label: 'Medium' },
            'high': { color: 'orange', label: 'High' },
            'urgent': { color: 'red', label: 'Urgent' }
        };
        return priorityMap[priority] || priorityMap['medium'];
    }, []);

    // Get category label
    const getCategoryLabel = useCallback((category) => {
        const categoryMap = {
            'hr': 'HR Related',
            'payroll': 'Payroll/Salary',
            'leave': 'Leave Management',
            'attendance': 'Attendance',
            'policy': 'Company Policy',
            'facility': 'Facility/Infrastructure',
            'it': 'IT Support',
            'general': 'General'
        };
        return categoryMap[category] || category;
    }, []);

    const value = useMemo(() => ({
        queries,
        myQueries,
        selectedQuery,
        comments,
        loading,
        error,
        dashboardStats,
        recentQueries,
        employeeGroups,
        pagination,
        employeePagination,
        myQueryStats,
        refreshTrigger,
        fetchQueries,
        fetchMyQueries,
        fetchQueryDetails,
        fetchComments,
        fetchQueryHistory,
        fetchDashboardStats,
        fetchRecentQueries,
        fetchEmployeeGroups,
        fetchMyQueryStats,
        createQuery,
        updateQuery,
        deleteQuery,
        addComment,
        deleteComment,
        assignQuery,
        resolveQuery,
        closeQuery,
        reopenQuery,
        refreshAllData,
        getStatusBadge,
        getPriorityBadge,
        getCategoryLabel,
        setSelectedQuery,
        setError,
        clearError: () => setError(null),
    }), [
        queries, myQueries, selectedQuery, comments, loading, error,
        dashboardStats, recentQueries, employeeGroups, pagination, employeePagination,
        myQueryStats, refreshTrigger,
        fetchQueries, fetchMyQueries, fetchQueryDetails, fetchComments,
        fetchQueryHistory, fetchDashboardStats, fetchRecentQueries,
        fetchEmployeeGroups, fetchMyQueryStats, createQuery, updateQuery, deleteQuery,
        addComment, deleteComment, assignQuery, resolveQuery, closeQuery,
        reopenQuery, refreshAllData, getStatusBadge, getPriorityBadge,
        getCategoryLabel
    ]);

    return (
        <QueryContext.Provider value={value}>
            {children}
        </QueryContext.Provider>
    );
};